// src/components/proposals/templateDefaults.js
//
// Proposal Templates hold the per-tier DEFAULT content that seeds a new
// proposal. Editing a template never touches existing proposals — a proposal
// deep-clones the defaults at creation and owns its copy from then on.
import { DEFAULT_BRONZE_CONTENT } from "./BronzeTemplate";
import { DEFAULT_SILVER_CONTENT } from "./SilverTemplate";
import { DEFAULT_FOUNDATIONAL_PRICING_NOTE } from "./foundationalBlocks";
import { DEFAULT_LEAD_CONTENT } from "./leadContent";
import { DOC_LEAD, docTypeOf } from "./docTypes";

/* Fields that are always customer-specific and therefore never live in a
   template's defaultContent. A new proposal fills these in fresh; the template
   editor hides the controls for them because they'd be meaningless there. */
export const CUSTOMER_FIELDS = [
  "customerName",
  "customerFullName",
  "customerContact",
  // Contract recipient address. Customer-specific by definition, so it is
  // deliberately NOT template-owned — a template must never carry one client's
  // email into the next proposal seeded from it.
  "customerEmail",
  "proposalDate",
  "salesRepName",
  "salesRepTitle",
  "deviceCount",
  "userCount",
  "coverImageId",
];

/* The Proposal-vs-Lead discriminator. Defined in docTypes.js — which is
   React-free, so opportunityPipeline.js can use it — and re-exported here so
   the proposal components have one import to reach for. */
export { DOC_PROPOSAL, DOC_LEAD, docTypeOf, isLead } from "./docTypes";

/* Strip the customer-specific fields out of a full content object, leaving the
   template-owned defaults (editorial copy, rates, page visibility). */
export function toDefaultContent(content) {
  const out = { ...content };
  CUSTOMER_FIELDS.forEach((k) => { delete out[k]; });
  return out;
}

export const formatMonthYear = (d) =>
  d.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

/* Seed set, created on first load. Bronze is seeded from the React component's
   own defaults, so behaviour is unchanged on day one — the template simply
   becomes the editable home of what was previously hardcoded.

   Silver and Gold are architectural placeholders: the plumbing accepts them,
   but there is no Claude Designs source for either yet, so they stay
   "coming-soon" and cannot be opened. */
export function DEFAULT_PROPOSAL_TEMPLATES() {
  const now = Date.now();
  return [
    {
      id: "tpl-bronze-sentinel",
      key: "bronze-sentinel",
      name: "Bronze Sentinel",
      tagline: "Managed Services — Foundation Tier",
      status: "active",
      defaultContent: toDefaultContent(DEFAULT_BRONZE_CONTENT()),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "tpl-silver-guardian",
      key: "silver-guardian",
      name: "Silver Guardian",
      tagline: "Managed Services — Enhanced Protection Tier",
      status: "active",
      defaultContent: toDefaultContent(DEFAULT_SILVER_CONTENT()),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "tpl-gold-fortress",
      key: "gold-fortress",
      name: "Gold Fortress",
      tagline: "Managed Services — Complete Protection Tier",
      status: "active",
      defaultContent: {
        introPara1: "", introPara2: "", introPara3: "",
        challenges: [], findings: [], foundationalBlocks: [],
        // Gold's own editable copy. Seeded from the shared constant so the
        // three tiers start out stating the caveat identically.
        foundationalPricingNote: DEFAULT_FOUNDATIONAL_PRICING_NOTE,

        recommendedPlan:  "Gold Fortress",
        perDeviceRate:     49,
        perUserRate:      120,
        // The fees carry their real amounts; waiveSetupFees decides whether
        // they are charged. Storing 0 would have thrown the numbers away, so
        // turning the waiver off later would show $0 instead of the real fee.
        onboardingFee:    625,
        intunePerDevRate: 110,   // standalone Intune add-on rate, same as Bronze
        intuneSetupFee:   599,
        waiveSetupFees:  true,   // typically waived on Gold — editable per proposal
        /* Copper Defender and the cross-tier Bronze grouping. Both are supplementary
           DEVICE groupings billed on the same contract as the tier itself.
           copperRate carries its real amount rather than 0 for the same reason the
           setup fees do: a zeroed rate throws the number away, and turning the
           toggle on later would then quote $0/device. */
        addOnCopper:                false,
        copperDeviceCount:  0,
        copperRate:                 15,
        addOnBronze:                false,
        bronzeDeviceCount:  0,
        addOnNetwork:     false,
        networkDeviceCount:  5,
        addOnBackup:      false,
        addOnSpamThreat:  false,
        addOnEmailSignature: false,
        showM365Standard:  true,
        m365PremiumQty:    null,
        additionalRows:      [],

        showIntro: true, showWhatHeard: true, showWhatFound: true,
        showRecommend: true, showComparison: true, showInvestment: true,
        showResponseTerms: true, showFoundational: true, showWhyUs: true,
      },
      createdAt: now,
      updatedAt: now,
    },
    /* The Lead pre-proposal. Same array, same seeding and immutability rules as
       a tier template — it is a document type, not a new subsystem. What
       differs is the SHAPE of defaultContent: a tier template's defaults
       describe one customer's proposal, this one is a shared service catalog
       that is identical on every Lead sent. */
    {
      id: "tpl-lead-preproposal",
      key: "lead-preproposal",
      name: "Pre-Proposal Overview",
      tagline: "Service tier comparison — sent before the full proposal",
      status: "active",
      docType: DOC_LEAD,
      defaultContent: toDefaultContent(DEFAULT_LEAD_CONTENT()),
      createdAt: now,
      updatedAt: now,
    },
  ];
}

/* Reconcile stored templates against the current seed set.

   The seed only runs when nothing is stored, so a tier that ships LATER would
   never reach an existing user: Gold sat in every blob as a "coming-soon"
   placeholder, and activating it in code alone would have left it greyed out
   forever. This upgrades a placeholder once its real template exists, adds any
   brand-new tier, and otherwise does not touch a stored record — edits to an
   active template's defaultContent are the user's and must survive. */
export function mergeTemplates(stored, defaults = DEFAULT_PROPOSAL_TEMPLATES()) {
  if (!Array.isArray(stored) || !stored.length) return defaults;
  const pending = new Map(defaults.map((t) => [t.key, t]));

  const merged = stored.map((s) => {
    const d = pending.get(s.key);
    if (!d) return s;                 // stored tier we no longer seed — leave alone
    pending.delete(s.key);
    // Placeholder that has since shipped: adopt the real record, keeping its
    // original createdAt so the card's history reads honestly.
    if (s.status === "coming-soon" && d.status === "active") {
      return { ...d, createdAt: s.createdAt || d.createdAt };
    }
    // Additive backfill: a field introduced after this blob was written (the
    // waiveSetupFees toggle) would otherwise be undefined forever on a stored
    // template. Only ever ADDS absent keys — an existing value is the user's
    // and is never overwritten.
    const backfilled = { ...d.defaultContent, ...s.defaultContent };
    return { ...s, defaultContent: backfilled };
  });

  return [...merged, ...pending.values()];   // tiers added since this blob was written
}

/* The whole proposal record, not just its content.

   Lives here rather than in ProposalsTab because a proposal can now also be
   created from an Opportunity, and two independent literals would drift — the
   second one would quietly miss whichever field was added to the first. Every
   caller gets the same shape; `extra` carries what the entry point knows
   (opportunityId, a pre-filled customer name). */
/* ⚠ THE API CLIENT IS IMPORTED LAZILY, NOT AT THE TOP OF THIS FILE.
   apiClient constructs the MSAL PublicClientApplication at module load, and
   jsdom has no WebCrypto — so a static import here makes every test that
   touches templateDefaults die on `crypto_nonexistent` before its first
   assertion. That is the documented reason proposalStatus.js and
   opportunityPipeline.js exist as bare modules, and this file is imported by
   most of the proposal suites.

   Deferring it keeps the module graph clean for anything that never actually
   creates a proposal, which is every test here. */
async function defaultReserve(name) {
  const { reserveNumber } = await import("../../api/sequenceApi");
  return reserveNumber(name);
}

/* ⚠ ASYNC, AND THAT IS DELIBERATE. The P number comes from an atomic
   server-side counter — it cannot be worked out in the browser, because
   proposals live in a state blob overwritten whole with no precondition, so a
   number derived from the records can be taken twice.

   Reserving INSIDE the factory rather than at the call sites is the whole
   point: this is the one place both entry points already share ("+ New
   Proposal" and Opportunities' "Create proposal"), and asking each of them to
   remember to reserve first is exactly the two-independent-literals drift the
   factory exists to prevent.

   Forgetting to `await` this now yields a Promise where a record is expected,
   which breaks loudly and immediately — far better than a record that is
   quietly missing its number.

   `reserve` is injectable so the rules can be tested without a backend. */
export async function makeProposalRecord(
  template,
  { salesRepName, extra = {}, content = {}, reserve = defaultReserve } = {},
) {
  const { content: contentExtra, ...rest } = extra;
  const docType = docTypeOf(template);

  /* ⚠ Leads take no number — Craig's decision, and the same asymmetry
     opportunityPipeline already carries for them. */
  let proposalNumber = null;
  if (docType !== DOC_LEAD) {
    try {
      proposalNumber = (await reserve("proposal")).number;
    } catch (e) {
      /* A reservation failure must NOT lose the user's work. The record is
         created unnumbered and the idempotent backfill picks it up on the next
         load, which is exactly the case it was written to handle. */
      // eslint-disable-next-line no-console
      console.warn("Could not reserve a proposal number; backfill will assign one.", e);
    }
  }

  return {
    proposalNumber,
    id: `prop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: docType === DOC_LEAD ? "New Lead" : "New Proposal",
    // No companyId/companyName: the Autotask link is gone, and every display
    // path already falls back to content.customerName.
    templateKey: template.key,
    // Written explicitly even for proposals, so records created from here on
    // carry it. Readers still go through docTypeOf() — every record written
    // before this shipped has no docType and must keep counting as a proposal.
    docType,
    status: "draft",
    content: {
      ...seedContentFromTemplate(template, { salesRepName }),
      ...content,
      ...(contentExtra || {}),
    },
    coverImages: {},          // id -> base64 data URL, stored once each
    quoteId: null,
    opportunityId: null,      // set by the Opportunity entry point / reverse picker
    snapshots: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
    // Stage 4 fields — prepared, deliberately inert in v1
    publicToken: null,
    publicUrl: null,
    clickTracking: [],
    ...rest,
  };
}

/* Build a new proposal's content by layering customer blanks over the
   template's defaults. Deep-cloned so later template edits can't reach in.

   The base layer is per docType. A Lead seeded from DEFAULT_BRONZE_CONTENT()
   would carry recommendedPlan, the nine proposal page-visibility flags and the
   whole pricing block — dead fields that nothing renders but that the settings
   panel and any future migration would have to keep explaining. */
export function seedContentFromTemplate(template, { salesRepName, salesRepTitle } = {}) {
  const defaults = JSON.parse(JSON.stringify(template?.defaultContent || {}));
  const lead = docTypeOf(template) === DOC_LEAD;
  const base = lead ? DEFAULT_LEAD_CONTENT() : DEFAULT_BRONZE_CONTENT();

  const customer = {
    // Customer-specific — always fresh, never inherited from the template
    customerName: "",
    customerFullName: "",
    customerContact: "",
    customerEmail: "",
    proposalDate: formatMonthYear(new Date()),
    salesRepName: salesRepName || "",
    salesRepTitle: salesRepTitle || "Managing Director",
    coverImageId: null,
  };

  return {
    ...base,                       // guarantees every field exists
    ...defaults,                   // template-owned overrides
    ...customer,
    // A Lead quotes no quantities — it shows rates, not a priced bill of
    // materials — so it deliberately carries no device/user count.
    ...(lead ? {} : { deviceCount: 5, userCount: 5 }),
  };
}
